import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { 
  Download, 
  Music, 
  Trophy, 
  Gamepad2, 
  Award, 
  Share2, 
  Star,
  Cloud,
  Smartphone,
  User,
  BarChart3,
  ChevronDown
} from 'lucide-react';

const LandingPage = () => {
  const [fartCount, setFartCount] = useState(335);
  const [showPuff, setShowPuff] = useState(false);

  // Fart sound URLs (free sound effects)
  const fartSounds = [
    'https://www.soundjay.com/human/fart-01.mp3',
    'https://www.soundjay.com/human/fart-02.mp3',
    'https://www.soundjay.com/human/fart-03.mp3',
  ];

  const playFartSound = useCallback(() => {
    const randomSound = fartSounds[Math.floor(Math.random() * fartSounds.length)];
    const audio = new Audio(randomSound);
    audio.volume = 0.5;
    audio.play().catch(e => console.log('Audio play failed:', e));
  }, []);

  const handleFart = useCallback(() => {
    setFartCount(prev => prev + 1);
    setShowPuff(true);
    playFartSound();
    setTimeout(() => setShowPuff(false), 500);
  }, [playFartSound]);

  return (
    <div className="min-h-screen bg-[#1a1a2e]">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cloud className="w-8 h-8 text-[#39FF14]" />
            <span className="font-bold text-xl text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              FART BUTTON
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-[#A0A0C0] hover:text-white transition-colors" data-testid="nav-features">Features</a>
            <a href="#app" className="text-[#A0A0C0] hover:text-white transition-colors" data-testid="nav-app">App</a>
            <a href="#download" className="text-[#A0A0C0] hover:text-white transition-colors" data-testid="nav-download">Download</a>
          </nav>
          <button 
            className="cta-button text-sm"
            data-testid="header-download-btn"
            onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Get App
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            {/* Left content */}
            <div className="flex-1 text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 
                  className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mb-6 neon-glow"
                  style={{ fontFamily: 'Unbounded, sans-serif', color: '#39FF14' }}
                  data-testid="hero-title"
                >
                  FART<br />BUTTON
                </h1>
                
                <p className="text-xl sm:text-2xl text-[#FFD700] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  1000+ Real Sounds from Freesound!
                </p>
                
                <p className="text-lg text-[#A0A0C0] mb-8 max-w-md">
                  The ultimate fart soundboard app. Collect badges, climb the leaderboard, and prove you're a true Grand Master of flatulence!
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    className="cta-button flex items-center justify-center gap-2"
                    data-testid="hero-download-btn"
                    onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <Download className="w-5 h-5" />
                    Download Free
                  </button>
                  <button 
                    className="store-button text-white"
                    data-testid="hero-learn-more-btn"
                    onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Learn More
                    <ChevronDown className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            </div>

            {/* Right - Phone Mockup with App UI */}
            <div className="flex-1 flex items-center justify-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                {/* Phone Frame */}
                <div className="relative bg-[#0a0a15] rounded-[3rem] p-3 shadow-2xl shadow-[#39FF14]/20 border border-white/10">
                  {/* Phone Screen */}
                  <div className="bg-[#1a1a2e] rounded-[2.5rem] overflow-hidden w-[280px] sm:w-[320px] p-6">
                    {/* App Content */}
                    <div className="flex flex-col items-center">
                      {/* Title */}
                      <div className="flex items-center gap-2 mb-1">
                        <span 
                          className="text-2xl font-black text-[#39FF14]"
                          style={{ fontFamily: 'Unbounded, sans-serif', textShadow: '0 0 10px rgba(57,255,20,0.5)' }}
                        >
                          FART BUTTON
                        </span>
                        <div className="w-7 h-7 rounded-full bg-[#39FF14] flex items-center justify-center">
                          <User className="w-4 h-4 text-[#1a1a2e]" />
                        </div>
                      </div>
                      
                      <p className="text-xs text-[#FFD700] italic mb-1">1000+ Real Sounds from Freesound!</p>
                      <p className="text-xs text-[#A0A0C0] mb-3">👋 moneyguy28yt</p>

                      {/* Farts Counter */}
                      <div className="bg-[#2A3A3A] rounded-full px-4 py-2 flex items-center gap-2 mb-2">
                        <Cloud className="w-4 h-4 text-[#39FF14]" />
                        <span className="text-sm font-bold text-[#39FF14]" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                          Farts: {fartCount}
                        </span>
                      </div>

                      {/* Press to Fart */}
                      <button 
                        className="bg-[#3A3A4A] rounded-full px-6 py-2 mb-4 w-full"
                        onClick={handleFart}
                      >
                        <span className="text-sm font-bold text-[#FFD700]">Press to Fart!</span>
                      </button>

                      {/* Fart Button */}
                      <div className="relative mb-4">
                        <AnimatePresence>
                          {showPuff && (
                            <motion.div
                              initial={{ scale: 0.5, opacity: 0.8 }}
                              animate={{ scale: 2, opacity: 0 }}
                              exit={{ opacity: 0 }}
                              className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
                            >
                              <Cloud className="w-16 h-16 text-white/60" />
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <motion.button
                          onClick={handleFart}
                          whileTap={{ scale: 0.95 }}
                          className="w-32 h-32 rounded-full flex items-center justify-center cursor-pointer"
                          style={{
                            background: 'linear-gradient(180deg, #D2691E 0%, #CD853F 50%, #8B4513 100%)',
                            border: '3px solid #8B4513',
                            boxShadow: '0 6px 0 #654321, 0 0 30px rgba(210,105,30,0.4)'
                          }}
                          data-testid="fart-button"
                        >
                          <div className="text-center">
                            <Cloud className="w-8 h-8 text-white/90 mx-auto" />
                            <span className="text-lg font-black text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                              FART!
                            </span>
                          </div>
                        </motion.button>
                      </div>

                      {/* Buttons Grid */}
                      <div className="w-full space-y-2">
                        <div className="flex gap-2">
                          <button className="flex-1 bg-transparent border-2 border-[#FF00FF] rounded-full px-3 py-2 flex items-center justify-center gap-1">
                            <Trophy className="w-4 h-4 text-[#FF00FF]" />
                            <span className="text-xs font-bold text-[#FF00FF]">Grand Master</span>
                          </button>
                          <button className="w-10 h-10 bg-[#0088FF] rounded-xl flex items-center justify-center">
                            <Share2 className="w-4 h-4 text-white" />
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <button className="flex-1 bg-[#3A3A4A] rounded-xl px-3 py-2 flex items-center justify-center gap-2">
                            <Award className="w-4 h-4 text-[#FFD700]" />
                            <span className="text-xs font-bold text-white">Badges</span>
                          </button>
                          <button className="flex-1 bg-[#3A3A4A] rounded-xl px-3 py-2 flex items-center justify-center gap-2">
                            <BarChart3 className="w-4 h-4 text-[#39FF14]" />
                            <span className="text-xs font-bold text-white">Scores</span>
                          </button>
                        </div>
                        <button className="bg-[#3A3A4A] border-2 border-[#FF6B6B] rounded-xl px-4 py-2 flex items-center justify-center gap-2 mx-auto">
                          <Gamepad2 className="w-4 h-4 text-[#FF6B6B]" />
                          <span className="text-xs font-bold text-white">Games</span>
                        </button>
                      </div>

                      <p className="text-xs text-[#A0A0C0] mt-3">Every fart is unique!</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee Section */}
      <div className="py-6 overflow-hidden border-y border-white/5">
        <Marquee gradient={false} speed={50}>
          <span className="marquee-text mx-8">1000+ SOUNDS</span>
          <span className="marquee-text mx-8">•</span>
          <span className="marquee-text mx-8">GRAND MASTER</span>
          <span className="marquee-text mx-8">•</span>
          <span className="marquee-text mx-8">EVERY FART IS UNIQUE</span>
          <span className="marquee-text mx-8">•</span>
          <span className="marquee-text mx-8">COLLECT BADGES</span>
          <span className="marquee-text mx-8">•</span>
          <span className="marquee-text mx-8">CLIMB LEADERBOARDS</span>
          <span className="marquee-text mx-8">•</span>
        </Marquee>
      </div>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#39FF14] mb-4 block">
              Features
            </span>
            <h2 
              className="text-3xl sm:text-4xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="features-title"
            >
              Why Millions Love It
            </h2>
          </motion.div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Music, color: '#39FF14', title: '1000+ Sounds', desc: 'Real fart sounds from Freesound.org - authentic and hilarious!' },
              { icon: Award, color: '#FFD700', title: 'Collect Badges', desc: 'Unlock achievements and show off your flatulence expertise!' },
              { icon: Trophy, color: '#FF00FF', title: 'Leaderboards', desc: 'Compete globally. Can you reach Grand Master rank?' },
              { icon: Gamepad2, color: '#FF6B6B', title: 'Mini Games', desc: 'Rhythm games, timing challenges, and more fun!' },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="feature-card text-center"
                data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <feature.icon className="w-12 h-12 mx-auto mb-4" style={{ color: feature.color }} />
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-[#A0A0C0]">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* App Preview Section */}
      <section id="app" className="py-24 px-6 bg-[#2A2A4A]/20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex-1"
            >
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#39FF14] mb-4 block">
                How It Works
              </span>
              <h2 
                className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                Simple & Fun
              </h2>
              <div className="space-y-6">
                {[
                  { step: '01', title: 'Press the Button', desc: 'Tap the big orange button to unleash hilarious sounds' },
                  { step: '02', title: 'Earn Points', desc: 'Every fart earns you points towards your rank' },
                  { step: '03', title: 'Collect & Compete', desc: 'Unlock badges and climb the global leaderboards' },
                ].map((item, index) => (
                  <motion.div
                    key={item.step}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#39FF14] font-bold">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-[#A0A0C0]">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex-1 flex justify-center"
            >
              <div className="bg-[#2A2A4A] border border-white/10 rounded-3xl p-8 max-w-md">
                <div className="text-center">
                  <Cloud className="w-16 h-16 text-[#39FF14] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    Ready to Fart?
                  </h3>
                  <p className="text-[#A0A0C0] mb-6">
                    Download now and start your journey to becoming a Grand Master of flatulence!
                  </p>
                  <button 
                    className="cta-button w-full"
                    onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Get the App
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="py-24 px-6 bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Smartphone className="w-16 h-16 text-[#39FF14] mx-auto mb-6" />
            <h2 
              className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="download-title"
            >
              Get Fart Button Now
            </h2>
            <p className="text-[#A0A0C0] text-lg mb-8 max-w-xl mx-auto">
              Download for free on iOS and Android. Every fart is unique!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://apps.apple.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="store-button text-white justify-center"
                data-testid="app-store-btn"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs text-[#A0A0C0]">Download on the</div>
                  <div className="font-bold">App Store</div>
                </div>
              </a>
              
              <a 
                href="https://play.google.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="store-button text-white justify-center"
                data-testid="play-store-btn"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 20.5v-17c0-.89.89-1.47 1.68-1.09l14.26 7.5c.76.4.76 1.5 0 1.9l-14.26 7.5c-.79.38-1.68-.2-1.68-1.09l-.01-.01"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs text-[#A0A0C0]">Get it on</div>
                  <div className="font-bold">Google Play</div>
                </div>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Cloud className="w-6 h-6 text-[#39FF14]" />
              <span className="font-bold text-lg text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                FART BUTTON
              </span>
            </div>
            
            <div className="flex items-center gap-6 text-[#A0A0C0] text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</Link>
              <Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-contact">Contact</Link>
            </div>

            <p className="text-[#A0A0C0] text-sm">
              © 2026 Fart Button. Every fart is unique!
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
